/**
 * Pure money module (design 2026-08-05 §A + D20).
 * Twin: src/shared/money.ts — keep the two in lockstep; the twin test
 * suites run the same canonical vectors so they cannot drift.
 *
 * D20 (download/export encoding): every string this module produces is
 * ASCII-only — en-US digit grouping, a plain "$", and a trailing ordinary
 * space before the display code ("$1,650.00 CAD"). Never locale currency
 * formatting, which legitimately emits "CA$", U+00A0 or U+202F — characters
 * pdfkit's embedded fonts can render as missing glyphs on signed documents.
 *
 * Display rule (§A): the code is ALWAYS printed, because USD and CAD share
 * the same dollar glyph and a bare "$1,650.00" on a Canadian proposal is
 * indistinguishable from USD.
 */

export interface MoneyCurrency {
  code: string;
  symbol: string;
  displayCode: string;
  decimalPlaces: number;
  roundingIncrement: number;
  /**
   * Authoritative "this is the app's base currency" flag (Task 2.9, M1
   * final-review fix). shouldShowCurrencyCode reads ONLY this field — never
   * `usdRate === 1` — because a currency can be pegged 1:1 to USD without
   * being the base currency, and a pegged-but-non-base currency must still
   * show its code.
   */
  isBase: boolean;
  /** 1 USD = usdRate units of this currency; required only by convertFromBase. */
  usdRate?: number;
}

/**
 * Round to the currency's rounding increment (0.01 for USD/CAD), FP-safe:
 * the quotient is quantized via toFixed before Math.round so classic float
 * artifacts (1.13 / 0.05 = 22.599999…) round the way money should.
 *
 * Bugfix (post-commit 32ca3f79): rounds the MAGNITUDE (Math.abs), then
 * reapplies the original sign, rather than rounding the signed amount
 * directly. Two reasons, both verified against a live
 * `Intl.NumberFormat('en-US',{style:'currency',currency:'USD'})`:
 *   1. Plain `Math.round` breaks ties toward +Infinity, which for a negative
 *      tie (-1.005 → -100.5 cents) rounds TOWARD zero (-100 → -1.00) instead
 *      of away from it (-101 → -1.01) like Intl's currency formatting does.
 *      Rounding the abs value means ties always round away from zero.
 *   2. A negative amount whose rounded magnitude is zero (e.g. -0.001) must
 *      round to -0, not +0, so formatMoney's sign check can still print "-"
 *      (legacy renders `format(-0.001)` as "-$0.00", never "$0.00"). Negating
 *      a signed amount that rounds to exactly 0 loses the sign under plain
 *      Math.round; negating the unsigned roundedAbs (`-roundedAbs`) preserves
 *      it, since JS's unary `-0` is `-0`.
 */
export function roundMoney(amount: number, currency: MoneyCurrency): number {
  const inc =
    currency.roundingIncrement > 0 ? currency.roundingIncrement : Math.pow(10, -currency.decimalPlaces);
  const isNegative = amount < 0 || Object.is(amount, -0);
  const absAmount = Math.abs(amount);
  const quotient = Math.round(Number((absAmount / inc).toFixed(6)));
  const roundedAbs = Number((quotient * inc).toFixed(6));
  return isNegative ? -roundedAbs : roundedAbs;
}

export interface FormatMoneyOptions {
  /** Append the display code ("$1,650.00 CAD"). Default true — the safe default (D21). */
  withCode?: boolean;
  /** Legacy formatCurrency semantics: pass 0 to trim trailing zeros ("$1,650", "$1,650.5"). */
  minDecimalPlaces?: number;
}

/**
 * C1 (final-review fix, 2026-08-06): for a STANDARD currency (rounding
 * increment == the natural 10^-decimalPlaces step — true for USD/CAD's
 * 0.01), skip roundMoney's pre-round and hand Intl.NumberFormat the raw
 * amount, exactly like every legacy call site did (`new
 * Intl.NumberFormat(...).format(amount)` on the unquantized double). Intl's
 * own rounding operates on the full-precision double and agrees with legacy
 * to the cent; roundMoney's toFixed(6) quantization step, by contrast,
 * snaps FP noise on a computed double (e.g. 1924.9649999999997, the result
 * of real arithmetic) onto an exact half-cent tie (192496.500000) that then
 * rounds UP — a full cent off from legacy's "$1,924.96". Pinned by the C1
 * fixtures in money.golden.test.ts / formatters.golden.test.ts.
 *
 * roundMoney itself is UNCHANGED and still used here for a genuinely
 * non-standard increment (e.g. a coarser 0.05 nickel-rounding currency),
 * where Intl has no notion of the custom increment and must be pre-rounded
 * — and by convertFromBase, which always needs a concrete rounded number to
 * return, not just a display string.
 */
export function formatMoney(amount: number, currency: MoneyCurrency, opts?: FormatMoneyOptions): string {
  const natural = Math.pow(10, -currency.decimalPlaces);
  const needsPreRound = currency.roundingIncrement > 0 && Math.abs(currency.roundingIncrement - natural) > 1e-12;
  const rounded = needsPreRound ? roundMoney(amount, currency) : amount;
  const body = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: opts?.minDecimalPlaces ?? currency.decimalPlaces,
    maximumFractionDigits: currency.decimalPlaces,
  }).format(Math.abs(rounded));
  // Object.is check: `rounded < 0` is false for -0, which would silently drop
  // the sign for a negative amount that rounded to zero magnitude (bugfix,
  // post-commit 32ca3f79) — see roundMoney's docstring.
  const sign = rounded < 0 || Object.is(rounded, -0) ? '-' : '';
  const base = `${sign}${currency.symbol}${body}`;
  return (opts?.withCode ?? true) ? `${base} ${currency.displayCode}` : base;
}

/**
 * Convert a base-currency (USD) amount at the admin-maintained rate and round
 * to the target's increment. Throws when the rate is unset — a currency row
 * whose placeholder rate was never configured must fail loudly, not convert 1:1.
 */
export function convertFromBase(amountInBase: number, currency: MoneyCurrency): number {
  if (currency.usdRate == null || !(currency.usdRate > 0)) {
    throw new Error(`Currency ${currency.code} has no usdRate set`);
  }
  return roundMoney(amountInBase * currency.usdRate, currency);
}

/**
 * D21: the code prints once a second currency is active — and ALWAYS for a
 * non-base currency, so an old CAD document can never render bare even if
 * CAD is later deactivated. While USD is alone, USD output is byte-identical
 * to the pre-multi-currency portal.
 *
 * Task 2.9 (M1 final-review fix): driven by `currency.isBase`, not a
 * `usdRate === 1` inference — a currency pegged 1:1 to USD is not
 * necessarily the base currency, and must still show its code.
 */
export function shouldShowCurrencyCode(currency: MoneyCurrency, activeCurrencyCount: number): boolean {
  return !currency.isBase || activeCurrencyCount > 1;
}

/**
 * Canonical USD descriptor for every legacy call site being swapped onto
 * formatMoney (Task 1.3). While USD is the only active currency, formatMoney
 * with this descriptor + { withCode: false } is byte-identical to the
 * pre-multi-currency `Intl.NumberFormat("en-US",{style:"currency",currency:"USD"})`.
 */
export const USD_CURRENCY: MoneyCurrency = {
  code: 'USD',
  symbol: '$',
  displayCode: 'USD',
  decimalPlaces: 2,
  roundingIncrement: 0.01,
  isBase: true,
  usdRate: 1,
};
